import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, MapPinOff } from 'lucide-react';

/**
 * The three reasons an artwork upload surface has no target location, each
 * with its own surface. Shared by colour mode (`ColourArtworkGrid`) and the
 * Product updates focused view, beside `ArtworkTargetPicker`, which renders
 * the choice itself.
 *
 * Order matters: "still reading your locations" must never render as "you have
 * no locations". The empty state is a claim about the org's configuration, and
 * making it while the read is in flight sends an operator to Settings for
 * nothing (DESIGN.md section 15.1).
 *
 * Renders `children(locationId)` once a location is resolved.
 */
export function ArtworkLocationGate({
  isLoading,
  needsChoice,
  locationId,
  skeletonRows = 2,
  children,
}: {
  /** `locations.list` is still in flight. */
  isLoading: boolean;
  /** Candidates exist but the operator has not picked one. */
  needsChoice: boolean;
  /** The resolved location, or null. */
  locationId: string | null;
  /** How many tile placeholders the loading skeleton shows. */
  skeletonRows?: number;
  children: (locationId: string) => ReactNode;
}) {
  const { t } = useTranslation('productSync');

  if (isLoading) {
    return (
      <div
        className="space-y-2 rounded-lg border bg-background p-3"
        role="status"
        aria-busy="true"
        aria-label={t('colourArtwork.loadingLocation')}
      >
        <div className="h-4 w-40 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        {Array.from({ length: skeletonRows }, (_, i) => (
          <div key={i} className="h-16 w-full animate-pulse rounded bg-muted motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  if (needsChoice) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-amber-300 bg-amber-50/60 px-4 py-10 text-center">
        <MapPin className="h-6 w-6 text-amber-700" aria-hidden="true" />
        <p className="text-sm font-semibold text-amber-900">{t('colourArtwork.chooseLocationTitle')}</p>
        <p className="mx-auto max-w-sm text-xs text-amber-800">{t('colourArtwork.chooseLocationBody')}</p>
      </div>
    );
  }

  if (!locationId) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border bg-muted/20 px-4 py-10 text-center">
        <MapPinOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-semibold">{t('colourArtwork.noLocationTitle')}</p>
        <p className="mx-auto max-w-sm text-xs text-muted-foreground">{t('colourArtwork.noLocationBody')}</p>
      </div>
    );
  }

  return <>{children(locationId)}</>;
}
